import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, RootState } from "redux/store";
import { api } from "api/axiosInterceptor";
import {
  decrementRequests,
  incrementRequests,
  setIsLoading,
  setShowToast,
} from "./application";
import {
  ISummaryState,
  RfpEvaluationResponse,
  ExistingSupplierData,
  ComparativeAnalysis,
  RfpVendorScore,
  Score,
  CategoryViewDetail,
  VendorEvaluation,
  ApiResponse,
  CategoryItem,
} from "types/summary";
import { apiUrl } from "api/apiUrl";
import { convertToCamelCase } from "utils/utils";

const initialState: ISummaryState = {
  projectDetails: {
    coreBusinessProblem: "",
    criticalCalDate: "",
    recommendedCompany: "",
    rfpTitle: "",
    userName: "",
    companyInfo: {},
    questions: {},
  },
  comparativeOverview: {
    rfpTitle: "",
    recommendedCompany: "",
    userName: "",
    criticalCalDate: "",
    extractionCategories: [],
    defaultCategoryType: "",
    companyInfo: {},
    reasoningAndComparativeAdvantages: {
      reasonForRecommendedVendor: "",
      keyComparativeAdvantages: [],
    },
    aiRecommanedVendor: {
      recommendedVendorName: "",
      reasoningForRecommendation: "",
      whyNotOthers: [],
      vendorHeading: {},
      rankedVendorList: {},
    },
    reasoningBehindAnalysis: [],
    pricingAnalysis: "",
    riskAnalysis: [],
    vendorCategoryLevel: [],
    rfpEvaluationCriteria: {},
    clientType: "",
    existingSupplier: undefined,
    revenueGenerating: false,
    subscription: "",
    chatbotQueryRes: {
      suggestedQuestions: []
    }
  },
  chatbotSuggestions: [],
  viewDetail: {
    company_info: {},
    questions: {},
    user_name: "",
    vendor_summary: {}
  },
  existingSupplier: {
    client_type: "",
    existing_supplier: {},
  },
  vendorScore: {
    extractionCategories: [],
    defaultCategoryType: "",
    overallWeight: 0,
    averageScore: {},
    assignAverageScore: {},
  },
  vendorEvaluation: {
    overallWeight: 0,
    averageScore: {},
    companyInfo: {
      data: {},
      heading: "",
    },
    budget: {
      data: "",
      heading: "",
    },
    coreBusinessProblem: {
      data: "",
      heading: "",
    },
    criticalCalDate: "",
    extractionCategories: [],
    pricingAnalysis: {
      data: [],
      heading: "",
    },
    primaryBusinessObjectives: {
      data: {
        primaryBusinessObjectives: [],
      },
      heading: "",
    },
    executiveSummary: {
      data: "",
      heading: "",
    },
    reasoningAndComparativeAdvantages: {
      data: [],
      heading: "",
    },
    reasoningBehindAnalysis: [],
    defaultCategoryType: "",
    recommendedCompany: "",
    riskAnalysis: {
      data: [],
      heading: "",
    },
    rfpTitle: "",
    vendorDetails: {
      data: {
        comments: "",
        vendorName: "",
        isExistingVendor: "",
        summary: "",
      },
      heading: "",
    },
  },
  guidelineDetails: {
    data: {},
    pagination: {
      pageLength: 0,
      pageNumber: 0,
      total: 0,
      totalPages: 0,
    },
    vendors: [],
    categories: [],
    priorities: [],
  },
  guidelineDetailsExport: {
    data: {}
  },
  negotiationStrategy: {
    data: [],
    executiveSummary: {
      executiveSummary: "",
      vendorProposedTCO: "",
      targetTCO: "",
      totalDirectSavingsRange: "",
      totalRiskMitigationValue: "",
      topNegotiationPriorities: [],
      targetTCOReduction: {
        valueRange: "",
        percentageRange: ""
      },
      batna: {
        primaryAlternative: {
          action: "",
          impact: ""
        },
        secondaryAlternative: {
          action: "",
          impact: ""
        },
        walkAwayPoint: ""
      },
      vendorIntelligence: {
        motivationsAndPressures: [],
        negotiationPrecedents: []
      }
    }
  },
};

const slice = createSlice({
  name: "application",
  initialState,
  reducers: {
    projectSummaryDetails(state, action: PayloadAction<RfpEvaluationResponse>) {
      state.projectDetails = action.payload;
    },
    comparativeAnalysis(state, action: PayloadAction<ComparativeAnalysis>) {
      state.comparativeOverview = action.payload;
    },
    rfpVendorScore(state, action: PayloadAction<RfpVendorScore>) {
      state.vendorScore = action.payload;
    },
    getCategoryViewDetail(state, action: PayloadAction<CategoryViewDetail>) {
      state.viewDetail = action.payload;
    },
    getExistingSupplierDetail(
      state,
      action: PayloadAction<ExistingSupplierData>
    ) {
      state.existingSupplier = action.payload;
    },
    getVendorEvaluation(state, action: PayloadAction<VendorEvaluation>) {
      state.vendorEvaluation = action.payload;
    },
    resetVendorEvaluation(state) {
      state.vendorEvaluation = initialState.vendorEvaluation;
    },
    setVendorGuidelineDetails(state, action: PayloadAction<ApiResponse>) {
      state.guidelineDetails.data = action.payload.data;
      state.guidelineDetails.pagination = action.payload.pagination;
      state.guidelineDetails.vendors = action.payload.vendors;
      state.guidelineDetails.categories = action.payload.categories;
      state.guidelineDetails.priorities = action.payload.priorities;
    },
    resetVendorGuidelineDetails(state){
      state.guidelineDetails.vendors = [];
    },
    setVendorGuidelineDetailsExport(state, action: PayloadAction<Pick<ApiResponse, "data">>) {
      state.guidelineDetailsExport.data = action.payload.data;
    },
    setNegotiationStrategy(
      state,
      action: PayloadAction<any>
    ) {
      state.negotiationStrategy.data = Object.values(action.payload?.data)?.flat() as CategoryItem[];
      state.negotiationStrategy.executiveSummary = action.payload.executiveSummary;
    },
    setChatbotSuggesstion(state, action: PayloadAction<string[]>) {
      state.chatbotSuggestions  = action.payload;
    },
    resetSuggestedQuestions(state) {
      state.comparativeOverview.chatbotQueryRes.suggestedQuestions = [];
    },
  },
});

export const {
  projectSummaryDetails,
  comparativeAnalysis,
  getCategoryViewDetail,
  rfpVendorScore,
  getExistingSupplierDetail,
  getVendorEvaluation,
  resetVendorEvaluation,
  setVendorGuidelineDetails,
  setVendorGuidelineDetailsExport,
  resetVendorGuidelineDetails,
  setNegotiationStrategy,
  resetSuggestedQuestions,
  setChatbotSuggesstion,
} = slice.actions;

export default slice.reducer;

export function getStarterAutomation() {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `/starter/automation/`;
      const response = await api.get(path);
      const formattedData = convertToCamelCase(response.data);

      dispatch(projectSummaryDetails(formattedData));
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function getProjectSummaryDetails() {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `/rfp/question/crtitical-requirements/`;
      const response = await api.get(path);
      const formattedData = convertToCamelCase(response.data);

      dispatch(projectSummaryDetails(formattedData));
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function getRfpComparativeAnalysis() {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `/rfp/reasoning/comparative/analysis/`;
      const response = await api.get(path);
      const formattedData = convertToCamelCase(response.data);
      dispatch(comparativeAnalysis(formattedData));
      dispatch(setChatbotSuggesstion(formattedData?.chatbotQueryRes?.suggestedQuestions || []));
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function categoryViewDetail(data: any) {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const path = `/rfp/category/view/details/`;
      const response = await api.post(path, data);
      dispatch(getCategoryViewDetail(response.data));
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
}

export function setExistingSupplier(data: any) {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `/rfp/existing/supplier/`;
      const response = await api.post(path, data);
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function getExistingSupplier() {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `/rfp/existing/supplier/`;
      const response = await api.get(path);
      dispatch(getExistingSupplierDetail(response.data));
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function setScoreDetail(data: Score) {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `/rfp/score/detail/`;
      const response = await api.post(path, data);
      return response.status;
    } catch (error : any) {
      const errorMessage =  error?.response?.data?.detail || "Something went wrong!";
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: errorMessage,
          toastDuration: 4000,
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function getVendorScore() {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `/rfp/vendor/scores/`;
      const response = await api.get(path);
      const formattedData = convertToCamelCase(response.data);
      dispatch(rfpVendorScore(formattedData));
      return response;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function updateVendorScore(data: any) {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `/rfp/vendor/scores/update/`;
      const response = await api.post(path, data);

      if(response.status === 200) {
        dispatch(getVendorScore());
        dispatch(
          setShowToast({
            show: true,
            type: "success",
            toastMessage: "Scorer updated successfully!",
          })
        );
      }

      return response;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function rfpMarketEvaluation() {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `/rfp/market/automation/eval/`;
      const response = await api.get(path);
      const formattedData = convertToCamelCase(response.data);
      dispatch(getVendorEvaluation(formattedData));
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function getVendorGuidelineDetails({
  pageNumber,
  allData = false,
  companyName = "",
  category = "",
  priority = "",
}: {
  pageNumber: number;
  allData?: boolean;
  companyName?: string;
  category?: string;
  priority?: string;
}) {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = apiUrl.vendorGuidelineDetails;
      const body = {
        page_length: 100,
        page_number: pageNumber,
        all_data: allData,
        company_name: companyName,
        category,
        priority,
      };
      const { data, status } = await api.post(path, body);
      const formattedData: ApiResponse = convertToCamelCase(data);
      if (status === 200) {
        dispatch(setVendorGuidelineDetails(formattedData));
      }
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export const getVendorGuidelineDetailsExport = ({
  vendors,
  category = "",
  priority = "",
}: {
  vendors: string[];
  category?: string;
  priority?: string;
}) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const path = apiUrl.vendorGuidelineDetailsExport;
      const body = {
        company_name: vendors,
        category,
        priority,
      };
      const { data, status } = await api.post(path, body);
      if (status === 200) {
        dispatch(setVendorGuidelineDetailsExport(data));
      }
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
};

export const sendQuestionnaireToVendor = (data: { company_name: string; email: string; questions: string[] }) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const path = apiUrl.sendQuestionnaireToVendor;
      const response = await api.post(path, data);
      dispatch(
        setShowToast({
          show: true,
          type: "success",
          toastMessage: response.data.message || "Questionnaire sent to vendor successfully!",
        })
      );
      
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Failed to send questionnaire to vendor. Please try again.",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
};

export function getNegotiationStrategy(company: string) {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `/negotiation-strategy-document/${company}/`;
      const response = await api.get(path);
      const formattedData = convertToCamelCase(response.data);
      dispatch(setNegotiationStrategy(formattedData));
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}


export const selectProjectDetails = (state: RootState) =>
  state.summaryData.projectDetails;
